/* eslint-disable eqeqeq */

import './ViewSuggestion.css';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Suggestion, Comment, boolqToNum, IsRejected, SuggestionStats, IsCompleted, IsOpen } from './Models';
import Loading from '../Loading';
import Endpoints from '../../endpoints';
import { Caste, User } from '../../App';
import { getFormattedDate, escapeHtml } from '../../helpers';
import Marked from 'marked';
import { UserContextMenu } from './UserContextMenu';
import Avatar from './Avatar';

interface Props {
    back: () => void;
    upvote: (id: number) => void;
    downvote: (id: number) => void;
    onCommentPosted: (sugId: number, commentId: number) => void;
    onCommentDeleted: (sugId: number, commentID: number) => void;
    forceSuggestionState: (sugId: number, isUserUpvoted: boolean | null, score: number) => void;
    onSuggestionReject: (sugId: number) => void;
    onSuggestionReopen: (sugId: number) => void;
    onSuggestionComplete: (sugId: number) => void;
    onSuggestionDelete: (sugId: number) => void;

    onBanStateChange: (id: string, banState: boolean) => void;
    onAdminStateChange: (id: string, caste: Caste) => void;

    user: User | null;
    stats: SuggestionStats;
}

interface State {
    suggestion: Suggestion | null;
    commentText: string;
}

export default class ViewSuggestion extends React.Component<Props & RouteComponentProps, State> {

    constructor(props: Props & RouteComponentProps) {
        super(props);
        this.state = {
            suggestion: null,
            commentText: ''
        };
        const [ep, method] = Endpoints.SUGGESTION_GET((props.match.params as { sid: number }).sid);
        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }
                const sug = await res.json() as Suggestion | null;
                this.setState({
                    suggestion: sug
                });
            })
            .catch((x) => console.log(x));

        this.postComment = this.postComment.bind(this);
        this.onTypingComment = this.onTypingComment.bind(this);
        this.discardComment = this.discardComment.bind(this);
        this.onCommentTextKeyDown = this.onCommentTextKeyDown.bind(this);
    }

    postComment() {

        if (this.state.suggestion == null) {
            return;
        }

        const [ep, method] = Endpoints.SUGGESTION_COMMENT_ADD(this.state.suggestion.id);
        fetch(ep, {
            method,
            body: JSON.stringify({
                text: this.state.commentText
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                const sug = this.state.suggestion;
                if (sug == null) {
                    return;
                }

                const model = sug as Suggestion;
                const comment = await res.json() as Comment;

                this.setState((prev) => ({
                    ...prev,
                    commentText: '',
                    suggestion: {
                        ...model,
                        comments: [
                            ...sug.comments,
                            comment
                        ]
                    }
                }));

                this.props.onCommentPosted(model.id, comment.id);
            })
            .catch((err) => console.log(err));
    }

    onTypingComment(data: string) {
        this.setState({
            commentText: data || ''
        });
    }

    upvoteComment(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_COMMENT_UPVOTE(id);
        fetch(ep, {
            method
        })
            .then(async (x) => this.handleVoteComment(x, id))
            .catch((x) => console.log(x));
    }

    downvoteComment(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_COMMENT_DOWNVOTE(id);
        fetch(ep, {
            method
        })
            .then(async (x) => this.handleVoteComment(x, id))
            .catch((x) => console.log(x));
    }

    upvoteSuggestion(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_UPVOTE(id);
        fetch(ep, {
            method
        })
            .then(async (x) => this.handleVoteSuggestion(x, id))
            .catch((x) => console.log(x));
    }

    downvoteSuggestion(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_DOWNVOTE(id);
        fetch(ep, {
            method
        })
            .then(async (x) => this.handleVoteSuggestion(x, id))
            .catch((x) => console.log(x));
    }

    async handleVoteSuggestion(res: Response, id: number) {
        if (!res.ok) {
            throw new Error(res.statusText);
        }

        const isUserUpvoted = await res.json();

        this.setState((prev) => {
            const sug = prev.suggestion;
            if (sug == null) {
                return prev;
            }

            const newScore = sug.score - boolqToNum(sug.isUserUpvoted) + boolqToNum(isUserUpvoted);
            this.props.forceSuggestionState(id, isUserUpvoted, newScore);
            return {
                ...prev,
                suggestion: {
                    ...sug,
                    isUserUpvoted,
                    score: newScore
                }
            };
        });
    }

    async handleVoteComment(res: Response, id: number) {
        if (!res.ok) {
            throw new Error(res.statusText);
        }

        const isUserUpvoted = await res.json();

        this.setState((prev) => {
            const sug = prev.suggestion;
            if (sug == null) {
                return prev;
            }

            return {
                ...prev,
                suggestion: {
                    ...sug,
                    comments: sug.comments.map((com) => com.id != id
                        ? com
                        : {
                            ...com,
                            isUserUpvoted,
                            score: com.score - boolqToNum(com.isUserUpvoted) + boolqToNum(isUserUpvoted)
                        })
                }
            };
        });
    }

    deleteComment(suggestionId: number, commentId: number) {
        const [ep, method] = Endpoints.SUGGESTION_COMMENT_DELETE(suggestionId, commentId);
        fetch(ep, {
            method
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }
                if (this.state.suggestion == null || this.state.suggestion.id != suggestionId) {
                    return;
                }

                const model = this.state.suggestion;
                this.setState((prev) => ({
                    ...prev,
                    suggestion: {
                        ...model,
                        comments: model.comments.filter((com) => com.id != commentId)
                    }
                }));

                this.props.onCommentDeleted(suggestionId, commentId);
            })
            .catch((err) => console.log(err));
    }

    deleteSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_DELETE(sugId);
        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.setState((prev) => {
                    return {
                        ...prev,
                        suggestion: null
                    };
                });
                this.props.onSuggestionDelete(sugId);
                this.props.history.push('/');
            })
            .catch((err) => console.log(err));
    }

    rejectSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_REJECT(sugId);
        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.setState((prev) => {
                    const model = prev.suggestion;
                    if (model == null) {
                        return prev;
                    }

                    return {
                        ...prev,
                        suggestion: {
                            ...model,
                            status: 'REJECTED'
                        }
                    };
                });
                this.props.onSuggestionReject(sugId);
            })
            .catch((err) => console.log(err));
    }

    completeSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_COMPLETE(sugId);
        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.setState((prev) => {
                    const model = prev.suggestion;
                    if (model == null) {
                        return prev;
                    }

                    return {
                        ...prev,
                        suggestion: {
                            ...model,
                            status: 'COMPLETED'
                        }
                    };
                });
                this.props.onSuggestionComplete(sugId);
            })
            .catch((err) => console.log(err));
    }

    reopenSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_REOPEN(sugId);
        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.setState((prev) => {
                    const model = prev.suggestion;
                    if (model == null) {
                        return prev;
                    }

                    return {
                        ...prev,
                        suggestion: {
                            ...model,
                            status: 'OPEN'
                        }
                    };
                });
                this.props.onSuggestionReopen(sugId);
            })
            .catch((err) => console.log(err));
    }

    discardComment() {
        this.setState({
            commentText: ''
        });
    }

    onCommentTextKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.shiftKey || event.key != 'Enter') {
            return;
        }

        this.postComment();
    }

    RenderComment: React.SFC<{ sug: Suggestion, com: Comment, user: User | null }> = (props) => {
        const com = props.com;
        const sug = props.sug;
        const upvoteClass = com.isUserUpvoted
            ? 'vote-counter--upvoted'
            : (com.isUserUpvoted == false ? 'vote-counter--downvoted' : '');

        return (
            <div className="comment">
                <div className={'vote-counter ' + upvoteClass}>
                    <div className="vote-counter__up" onClick={() => this.upvoteComment(com.id)}>
                        <svg viewBox="0 0 24 24">
                            <path fill="#000000" d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
                        </svg>
                    </div>
                    <div className="vote-counter__count">{com.score}</div>
                    <div className="vote-counter__down" onClick={() => this.downvoteComment(com.id)}>
                        <svg viewBox="0 0 24 24">
                            <path fill="#000000" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                        </svg>
                    </div>
                </div>
                <div className="comment__content">
                    <div className="comment__title">
                        <Avatar user={com.author} />
                        <UserContextMenu
                            user={sug.author}
                            invoker={this.props.user}
                            onBanStateChange={this.props.onBanStateChange}
                            onAdminStateChange={this.props.onAdminStateChange}
                        >
                            <span className="comment__username">{com.author.name}</span>
                        </UserContextMenu>
                        <span className="comment__date">{getFormattedDate(com.createdAt)}</span>
                    </div>
                    <div
                        className="comment__description"
                        dangerouslySetInnerHTML={({ __html: Marked.parse(escapeHtml(com.text)) })}
                    />
                </div>
                {props.user != null && (props.user.caste == Caste.Admin || props.user.id == com.author.id) && <div
                    className="icon-button icon-button--red"
                    onDoubleClick={() => this.deleteComment(sug.id, com.id)}
                >
                    <svg viewBox="0 0 24 24">
                        <path fill="#000000" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                    </svg>
                </div>}
            </div>
        );
    }

    render() {

        const sug = this.state.suggestion;

        if (sug == null) {
            return <Loading />;
        }

        const isAdmin = this.props.user == null ? false : this.props.user.caste == Caste.Admin;
        const isAuthor = this.props.user == null ? false : this.props.user.id == sug.author.id;
        const isCompleted = IsCompleted(sug);
        const isRejected = IsRejected(sug);
        const status = sug.status.toUpperCase();

        const upvoteClass = sug.isUserUpvoted
            ? 'vote-counter--upvoted'
            : (sug.isUserUpvoted == false ? 'vote-counter--downvoted' : '');

        const statusClass = (() => {
            switch (sug.status) {
                case 'OPEN':
                    return '';
                case 'COMPLETED':
                    return 'suggestion--completed';
                case 'REJECTED':
                default:
                    return 'suggestion--rejected';
            }
        })();

        return [
            <div key="suggestion" className={'suggestion suggestion--expanded ' + statusClass}>
                <div className={'vote-counter ' + upvoteClass}>
                    <div className="vote-counter__up" onClick={() => this.upvoteSuggestion(sug.id)}>
                        <svg viewBox="0 0 24 24">
                            <path fill="#000000" d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
                        </svg>
                    </div>
                    <div className="vote-counter__count">{sug.score}</div>
                    <div className="vote-counter__down" onClick={() => this.downvoteSuggestion(sug.id)}>
                        <svg viewBox="0 0 24 24">
                            <path fill="#000000" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                        </svg>
                    </div>
                </div>
                <div className="suggestion__content">
                    <div className="suggestion__header">
                        <div className="suggestion__title">
                            {sug.title}
                        </div>
                        <div className="suggestion__subtitle">
                            <Avatar user={sug.author} />

                            <UserContextMenu
                                user={sug.author}
                                invoker={this.props.user}
                                onBanStateChange={this.props.onBanStateChange}
                                onAdminStateChange={this.props.onAdminStateChange}
                            >
                                <span className="suggestion__username">{sug.author.name}</span>
                            </UserContextMenu>
                            <span>{getFormattedDate(sug.createdAt)}</span>
                            {/* <span>{sug.comments.length} comments</span> */}
                            <span className="suggestion__status">{status}</span>
                        </div>
                    </div>
                    <div
                        className="suggestion__description"
                        dangerouslySetInnerHTML={({ __html: Marked.parse(escapeHtml(sug.description)) })}>
                    </div>
                    <div className="suggestion__footer">
                        <div className="suggestion__actions">
                            {isAdmin && !IsOpen(sug) && <div
                                className="icon-button icon-button--blue"
                                onClick={() => this.reopenSuggestion(sug.id)}
                            >
                                <svg viewBox="0 0 24 24">
                                    <path fill="#000000" d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z" />
                                </svg>
                            </div>}
                            {!isCompleted && isAdmin && <div
                                className="icon-button icon-button--green"
                                onClick={() => this.completeSuggestion(sug.id)}
                            >
                                <svg viewBox="0 0 24 24">
                                    <path fill="#000000" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                                </svg>
                            </div>}
                            {!isRejected && (isAdmin || isAuthor) &&
                                <div
                                    className="icon-button icon-button--yellow"
                                    onClick={() => this.rejectSuggestion(sug.id)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path fill="#000000" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                                    </svg>
                                </div>}
                            {isAdmin &&
                                <div
                                    className="icon-button icon-button--red"
                                    onDoubleClick={() => this.deleteSuggestion(sug.id)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path fill="#000000" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                                    </svg>
                                </div>}
                        </div>
                    </div>
                </div>
            </div>,
            <div key="comments" className="comment-list">
                <div className="comment-list__header">
                    <span className="comment-list__count">Comments - {sug.comments.length}</span>
                    {this.props.user != null && this.state.commentText.trim() != '' && <div className="button button--red" onClick={this.discardComment}>
                        <span>Discard</span>
                        <div className="icon">
                            <svg viewBox="0 0 24 24">
                                <path fill="#000000" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                            </svg>
                        </div>
                    </div>}
                </div>
                {this.props.user != null && <input
                    type="text"
                    placeholder="Add comment"
                    value={this.state.commentText}
                    onChange={(event) => this.onTypingComment(event.target.value)}
                    onKeyDown={this.onCommentTextKeyDown}
                />}
                {sug.comments.map((com) => <this.RenderComment
                    key={com.id}
                    sug={sug}
                    com={com}
                    user={this.props.user} />)}
            </div>
        ];
    }
}